import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { useGlobalContext } from "../../Config";
import { normalizeFicha } from "../../utilities/fichaNormalizer";

const FichaTecnicaModal = ({ visible, onHide, productoId, productoDescripcion }) => {
    const { baseUrl } = useGlobalContext();
    const [fichaData, setFichaData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && productoId) {
            loadFichaTecnica();
        }
    }, [visible, productoId]);

    const loadFichaTecnica = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${baseUrl}getProductoFicha?idProducto=${productoId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json; charset=utf-8'
                }
            });
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            
            // Obtener el texto de la respuesta y parsearlo manualmente
            const textResponse = await response.text();
            const data = JSON.parse(textResponse);
            // console.log('Datos recibidos:', data); // Para debug
            
            // Validar que la respuesta tenga la estructura esperada
            if (!data || typeof data !== 'object') {
                throw new Error('Formato de respuesta inválido');
            }
            
            // Verificar que la respuesta sea exitosa
            if (!data.respuesta || !data.existe) {
                throw new Error(data.mensaje || 'Producto no encontrado');
            }
            
            setFichaData({
                ...data,
                ficha: normalizeFicha(data.ficha)
            });
        } catch (error) {
            console.error('Error completo:', error);
            setError(error.message || 'Error desconocido al cargar la ficha');
        } finally {
            setLoading(false);
        }
    };

    const renderFichaContent = () => {
        if (loading) {
            return (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                    <span className="ml-2">Cargando ficha técnica...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-2 text-600">{error}</p>
                </div>
            );
        }

        if (!fichaData || !fichaData.ficha) {
            return (
                <div className="text-center p-4">
                    <i className="pi pi-info-circle text-blue-500" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-2 text-600">No hay información de ficha disponible</p>
                </div>
            );
        }

        return (
            <div className="ficha-container">
                {/* Header con información del producto */}
                <div className="bg-primary-50 p-3 border-round mb-4">
                    <div className="flex align-items-center">
                        <i className="pi pi-file-o text-primary mr-2" style={{ fontSize: '1.5rem' }}></i>
                        <div>
                            <h4 className="m-0 text-primary">Ficha Técnica</h4>
                            <p className="m-0 text-600 text-sm">
                                Producto ID: {fichaData.idProducto} | Perfil: {fichaData.perfil}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Contenido de la ficha */}
                <div className="grid">
                    {Object.entries(fichaData.ficha).map(([seccion, campos]) => (
                        <div key={seccion} className="col-12 md:col-6">
                            <div className="card h-full">
                                <div className="card-header bg-blue-50 p-3 border-round-top">
                                    <h5 className="m-0 text-blue-800 capitalize">
                                        <i className="pi pi-list mr-2"></i>
                                        {seccion.replace('_', ' ')}
                                    </h5>
                                </div>
                                <div className="card-body p-3">
                                    {typeof campos === 'object' && campos !== null ? (
                                        <div className="field-list">
                                            {Object.entries(campos).map(([campo, valor]) => (
                                                <div key={campo} className="field-item mb-3 pb-2 border-bottom-1 surface-border">
                                                    <div className="flex justify-content-between align-items-start">
                                                        <span className="font-medium text-700 capitalize">
                                                            {campo.replace('_', ' ')}:
                                                        </span>
                                                        <span className="text-900 ml-2 text-right">
                                                            {typeof valor === 'object' ? 
                                                                JSON.stringify(valor, null, 2) : 
                                                                String(valor)
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-600 m-0">{String(campos)}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleHide = () => {
        setFichaData(null);
        setError(null);
        onHide();
    };

    return (
        <Dialog
            header={`Ficha Técnica - ${productoDescripcion || 'Producto'}`}
            visible={visible}
            className="p-fluid product-dialog"
            modal
            maximizable
            onHide={handleHide}
            headerStyle={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}
            contentStyle={{ padding: '1.5rem' }}
        >
            {renderFichaContent()}
        </Dialog>
    );
};

export default FichaTecnicaModal;